import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { Observable, from } from 'rxjs';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private msalService = inject(MsalService);
  private configService = inject(ConfigService);
  private router = inject(Router);
  
  
  isAuthenticated = signal<boolean>(false);
  currentAccount = signal<AccountInfo | null>(null);
  private initializationPromise: Promise<void>;

  constructor() {
    this.initializationPromise = this.initializeMsal();
    // Start initialization immediately
    this.initializationPromise.then(() => {
      this.handleRedirectObservable();
    });
  }

  private async initializeMsal(): Promise<void> {
    try {
      await this.msalService.instance.initialize();
      console.log('MSAL initialized successfully');
      this.checkInitialAuthState();
    } catch (error) {
      console.error('Error initializing MSAL:', error);
      this.isAuthenticated.set(false);
    }
  }

  private checkInitialAuthState(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.currentAccount.set(accounts[0]);
      this.isAuthenticated.set(true);
      console.log('User already authenticated');
    } else {
      this.isAuthenticated.set(false);
      console.log('User not authenticated');
      
      // Auto-initiate login if not authenticated and not in a redirect flow
      if (!window.location.hash.includes('code=') && !window.location.hash.includes('error=')) {
        setTimeout(() => {
          this.login().catch(err => console.error('Auto-login failed:', err));
        }, 100);
      }
    }
  }

  async login(): Promise<void> {
    await this.initializationPromise;
    
    // Check if there's already an interaction in progress
    if (this.msalService.instance.getActiveAccount() || 
        this.msalService.instance.getAllAccounts().length > 0) {
      console.log('User already authenticated, skipping login');
      return;
    }

    // Use redirect login
    try {
      this.msalService.loginRedirect({
        scopes: this.configService.scopes,
        redirectUri: this.configService.redirectUri
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('interaction_in_progress')) {
        console.log('Login interaction already in progress, skipping');
        return;
      }
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.initializationPromise;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: this.configService.postLogoutRedirectUri
    });
  }

  async getAccessToken(): Promise<string | null> {
    await this.initializationPromise;
    
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return null;
    }

    const request: SilentRequest = {
      scopes: this.configService.scopes,
      account: account
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error('Error acquiring token silently:', error);
      try {
        this.msalService.instance.acquireTokenRedirect(request);
        return null;
      } catch (redirectError) {
        console.error('Error acquiring token via redirect:', redirectError);
        return null;
      }
    }
  }

  handleRedirectObservable(): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (result) {
          this.msalService.instance.setActiveAccount(result.account);
          this.currentAccount.set(result.account);
          this.isAuthenticated.set(true);
          console.log('Authentication successful', result);
          
          // Force router to re-evaluate guards
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/']);
          });
        }
      },
      error: (error) => {
        console.error('Authentication error:', error);
        this.isAuthenticated.set(false);
      }
    });
  }

  async initializeAuthentication(): Promise<void> {
    await this.initializationPromise;
    
    // Handle any pending redirect first
    try {
      const redirectResult = await this.msalService.instance.handleRedirectPromise();
      if (redirectResult) {
        this.msalService.instance.setActiveAccount(redirectResult.account);
        this.currentAccount.set(redirectResult.account);
        this.isAuthenticated.set(true);
          console.log('Authentication successful after redirect');
        
        // Force router to re-evaluate guards
        setTimeout(() => {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/']);
          });
        }, 100);
        return;
      }
    } catch (error) {
      console.error('Error handling redirect:', error);
      this.isAuthenticated.set(false);
    }
    
    // If not authenticated and no redirect in progress, start login
    if (!this.isAuthenticated()) {
      console.log('User not authenticated, initiating login...');
      await this.login();
    }
  }
}